/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { ReadOnlyPropertiesMixin } from '@open-wc/lit-helpers';
import { css, html, LitElement } from 'lit-element';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { calculatedBaseFontSize } from '../../utils/base-font-size.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { getDecimalNumberPrecision } from '../../utils/decimal-number-precision.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { KEYCODE } from '../../utils/keycode.js';
import { SettingTooltipMixin } from '../../utils/setting-tooltip-mixin.js';
import { SliderInputRectifierMixin } from '../../utils/slider-input-rectifier-mixin.js';
import { SliderStepMarkerMixin } from '../../utils/slider-step-marker-mixin.js';

/**
 * @prop {Boolean} disabled - Default false. If set to `true`, user interaction won't be allowed and the styles of the component will change.
 * @prop {String} label - Label disabled in range slider.
 * @prop {Number} min - Default 0. Defines the minimum value allowed.
 * @prop {Number} max - Default 100. Defines the maximum value allowed.
 * @prop {Number} step - Default 1. Defines the intervals of the slider. If set to 0.1 for example, decimals will be allowed.
 * @prop {Boolean} showValue - Default false. If set to `true`, an input field will be shown allowing the user to change the value using keyboard input.
 * @prop {Number} upperValue - Default 0. Defines the current upper value of the range in a slider . Must be a number between min and max values and cannot be less than the lower value.
 * @attr {String} upper-value - Default 0. Defines the current upper value of the range in a slider . Must be a number between min and max values and cannot be less than the lower value.
 * @prop {Number} lowerValue - Default 0. Defines the current lower value of the range in a slider . Must be a number between min and max values and cannot be more than the upper value.
 * @attr {String} lower-value - Default 0. Defines the current lower value of the range in a slider . Must be a number between min and max values and cannot be more than the upper value.
 * @prop {Boolean} stepMarkers - *hide Shows markers if enabled.
 */
export class SHRangeSlider extends SettingTooltipMixin(
  SliderStepMarkerMixin(SliderInputRectifierMixin(ReadOnlyPropertiesMixin(LitElement)))
) {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      min: { type: Number, reflect: true },
      max: { type: Number, reflect: true },
      step: { type: Number, reflect: true },
      /** @ignore */
      showValue: { type: Boolean, reflect: true, attribute: 'show-value' },
      disabled: { type: Boolean, reflect: true },
      /** @ignore */
      lowerValue: { type: Number, reflect: true, attribute: 'lower-value' },
      /** @ignore */
      upperValue: { type: Number, reflect: true, attribute: 'upper-value' },
      stepMarkers: { type: Array, readOnly: true },
      /** @ignore */
      _selectedThumb: { type: String },
      /** @ignore */
      _isDrag: { type: Boolean, reflect: true },
    };
  }

  static get styles() {
    return [
      css`
        :host {
          width: 100%;
          outline: 0;
          display: block;
        }
        .range-wrapper {
          position: relative;
          height: 2rem;
          outline: none;
          display: flex;
          width: calc(100% - 1rem);
          margin: 0 0.5rem;
        }
        input[type='range']::-webkit-slider-thumb {
          box-shadow: none;
        }
        #lower::-webkit-slider-thumb,
        #upper::-webkit-slider-thumb {
          appearance: none;
          position: relative;
          width: 0.875rem;
          height: 0.875rem;
          cursor: pointer;
          border-radius: 50%;
          border: none;
          outline: 0;
          z-index: 1;
          background: rgba(var(--ui-5), var(--opacity-1));
          -webkit-appearance: none;
          user-select: none;
          -webkit-user-select: none;
        }
        #lower::-moz-range-thumb,
        #upper::-moz-range-thumb {
          position: relative;
          width: 0.875rem;
          height: 0.875rem;
          cursor: pointer;
          border-radius: 50%;
          border: none;
          z-index: 1;
          background: rgba(var(--ui-5), var(--opacity-1));
        }
        :host(.touch-device) #lower::-webkit-slider-thumb,
        :host(.touch-device) #upper::-webkit-slider-thumb {
          background-clip: padding-box;
          border: 0.8125rem solid rgba(0, 0, 0, 0);
          box-sizing: unset;
        }
        :host(.touch-device) #handleTrackWrapper {
          height: 2.5rem;
        }
        :host(.touch-device) #rangeTrack,
        :host(.touch-device) #lower,
        :host(.touch-device) #upper,
        :host(.touch-device) #handleTrack {
          margin-top: 1.15rem;
        }
        :host(.touch-device) #rangeTrack {
          border-top: 1.1875rem solid rgba(0, 0, 0, 0);
          border-bottom: 1.1875rem solid rgba(0, 0, 0, 0);
          background-clip: padding-box;
          margin-top: 0;
        }
        :host(.touch-device) .range-wrapper {
          height: 2.5rem;
        }
        :host(:not(.touch-device)) #rangeTrack {
          border-top: 0.375rem solid rgba(0, 0, 0, 0);
          border-bottom: 0.375rem solid rgba(0, 0, 0, 0);
          background-clip: padding-box;
          margin-top: 0.5625rem;
        }
        :host(:not(.touch-device)) #lower::-webkit-slider-thumb:hover,
        :host(:not(.touch-device)) #upper::-webkit-slider-thumb:hover {
          background: rgba(var(--ui-2), var(--opacity-1));
        }
        :host(:not(.touch-device)) #lower::-moz-range-thumb:hover,
        :host(:not(.touch-device)) #upper::-moz-range-thumb:hover {
          background: rgba(var(--ui-2), var(--opacity-1));
        }
        :host(.touch-device) .thumbActive#lower::-webkit-slider-thumb,
        :host(.touch-device) .thumbActive#upper::-webkit-slider-thumb {
          border: 0.8125rem solid rgba(var(--ui-3), var(--opacity-5));
          background-color: rgba(var(--ui-3), var(--opacity-1));
        }
        #lower::-webkit-slider-thumb:active,
        #upper::-webkit-slider-thumb:active {
          background: rgba(var(--ui-3), var(--opacity-1));
        }
        #lower::-moz-range-thumb:active,
        #upper::-moz-range-thumb:active {
          background: rgba(var(--ui-3), var(--opacity-1));
        }
        :host(.touch-device) #lower::-webkit-slider-runnable-track,
        :host(.touch-device) #upper::-webkit-slider-runnable-track {
          margin-left: -0.8125rem;
          margin-right: -0.8125rem;
        }
        .range-track {
          width: calc(100% + 1rem);
          height: 0.125rem;
          outline: 0;
          background-color: rgba(var(--ui-1), var(--opacity-6));
          cursor: pointer;
          position: absolute;
          -moz-appearance: none;
          -webkit-appearance: none;
          margin-left: -0.5rem;
          margin-top: 0.9375rem;
        }
        .handle-track {
          height: 0.125rem;
          background: rgba(var(--ui-5), var(--opacity-1));
          cursor: pointer;
          margin-top: 0.9375rem;
        }
        .handle-track-wrapper {
          height: 2rem;
          position: absolute;
        }
        #handleTrackWrapper {
          cursor: pointer;
          outline: 0;
        }
        #lower,
        #upper {
          background-color: transparent;
          opacity: unset;
          pointer-events: none;
        }
        #lower::-webkit-slider-thumb,
        #upper::-webkit-slider-thumb {
          position: relative;
          pointer-events: all;
        }
        #lower::-moz-range-thumb,
        #upper::-moz-range-thumb {
          position: relative;
          pointer-events: all;
        }
        :host(:not(.touch-device)) .handle-track-wrapper:hover .handle-track,
        .focus#lower::-webkit-slider-thumb,
        .focus#upper::-webkit-slider-thumb {
          background: rgba(var(--ui-2), var(--opacity-1));
        }
        .active#lower::-webkit-slider-thumb,
        .active#upper::-webkit-slider-thumb,
        .active#handleTrack {
          background: rgba(var(--ui-3), var(--opacity-1));
        }
        .focus#lower::-moz-range-thumb,
        .focus#upper::-moz-range-thumb {
          background: rgba(var(--ui-2), var(--opacity-1));
        }
        .active#lower::-moz-range-thumb,
        .active#upper::-moz-range-thumb {
          background: rgba(var(--ui-3), var(--opacity-1));
        }
        /* disabled styles */
        :host([disabled]) #upper::-webkit-slider-thumb,
        :host([disabled]) #lower::-webkit-slider-thumb,
        :host([disabled]) #handleTrack {
          background-color: rgba(var(--ui-6), var(--opacity-1));
          pointer-events: none;
        }
        :host([disabled]) #upper::-moz-range-thumb,
        :host([disabled]) #lower::-moz-range-thumb {
          background: rgba(var(--ui-6), var(--opacity-1));
          pointer-events: none;
        }
        :host([disabled]) #rangeTrack {
          background-color: rgb(var(--ui-1), var(--opacity-7));
        }

        .label-wrapper {
          display: flex;
          justify-content: space-between;
        }
        .slider-label {
          flex: 1 1 auto;
          min-width: 3.375rem;
          font: var(--body-1);
          line-height: 1.5rem;
          font-weight: normal;
          color: var(--text-secondary);
          padding-right: 0.5rem;
          -webkit-font-smoothing: antialiased !important;
          text-rendering: optimizeLegibility !important;
          -moz-osx-font-smoothing: grayscale !important;
          user-select: none;
          -moz-user-select: none;
          -webkit-user-select: none;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          transition: 0.2s all ease-in-out;
        }

        .label-wrapper input {
          outline: 0;
          border: none;
          width: 2.5rem;
          height: 1.5rem;
          font: var(--body-1);
          line-height: 1.5rem;
          color: var(--text-secondary);
          background: rgba(var(--ui-1), var(--opacity-7));
          border-radius: 0.125rem;
          box-sizing: border-box;
          padding: 0.25rem;
          transition: 0.2s all ease-in-out;
          text-align: center;
          -moz-appearance: textfield;
        }
        .value-separator {
          height: 1.5rem;
          width: 0.75rem;
          text-align: center;
          line-height: 1.5rem;
          color: rgba(var(--ui-5), var(--opacity-1));
        }
        :host([disabled]) .value-separator {
          color: rgba(var(--ui-6), var(--opacity-1));
        }
        input::-webkit-inner-spin-button {
          display: none;
        }

        :host(:not([label])) .label-wrapper {
          display: none;
        }

        :host([show-value]) .label-wrapper input {
          display: inline-flex;
        }

        :host([disabled]) .slider-label,
        :host([disabled]) .label-wrapper input {
          color: var(--text-disabled);
          opacity: unset;
        }

        /* stepTicks */
        .step_ticks {
          position: absolute;
          padding: 0;
          margin: 0 auto;
          top: 0;
          bottom: -0.03125rem;
          display: none;
          flex-direction: row;
          align-items: center;
        }

        .step_ticks li {
          height: 0.5rem;
          width: 0.0625rem;
          -moz-user-select: none;
          -webkit-user-select: none;
          list-style: none;
          position: absolute;
        }

        :host .sh-step-thumb-item {
          cursor: pointer;
          outline: 0;
          background: rgba(var(--ui-1), var(--opacity-6));
          -moz-appearance: none;
          -webkit-appearance: none;
          user-select: none;
          -moz-user-select: none;
          -webkit-user-select: none;
        }

        :host([disabled]) .sh-step-thumb-item {
          background: rgba(var(--ui-1), var(--opacity-7));
        }
        /* Tooltip styles */
        #lowerRangeTooltip,
        #upperRangeTooltip {
          display: none;
          position: absolute;
          pointer-events: none;
          outline: 0;
          width: 2.5rem;
          height: 1.5rem;
          font: var(--body-1);
          text-align: center;
          color: var(--text-secondary);
          background: var(--base-3);
          box-shadow: var(--shadow-raised);
          border-radius: 0.125rem;
          box-sizing: border-box;
          padding: 0.25rem;
          z-index: 5;
          margin: -1.375rem;
        }
        :host(:not([show-value]):hover:not(.touch-device)) #lowerRangeTooltip,
        :host(:not([show-value]):hover:not(.touch-device)) #upperRangeTooltip {
          display: block;
        }
        :host .markers.step_ticks {
          display: flex;
        }
        /* tab-outline */
        :host .tab_outline.handle-track-wrapper {
          outline: var(--focus-outline) !important;
        }
        :host .tab_outline.handle-track {
          background: rgba(var(--ui-2), var(--opacity-1));
        }
        :host(:not([show-value])) .tab_tooltip#lowerRangeTooltip,
        :host(:not([show-value])) .tab_tooltip#upperRangeTooltip {
          display: block;
        }
        :host .tab_outline#lower::-webkit-slider-thumb,
        :host .tab_outline#upper::-webkit-slider-thumb {
          outline: var(--focus-outline);
          background: rgba(var(--ui-2), var(--opacity-1));
        }

        :host .tab_outline#lower::-moz-range-thumb,
        :host .tab_outline#upper::-moz-range-thumb {
          outline: var(--focus-outline);
          background: rgba(var(--ui-2), var(--opacity-1));
        }
      `,
    ];
  }

  render() {
    return html`
      ${this.label
        ? html` <div class="label-wrapper">
            <div class="slider-label">${this.label}</div>
            ${this.showValue
              ? html` <input
                    id="lowerInputText"
                    type="number"
                    min="${this.min}"
                    max="${this.max}"
                    step="${this.step}"
                    .value="${this.lowerValue}"
                    maxlength="4"
                    ?disabled="${this.disabled}"
                    @keyup="${this._onKeyPress}"
                    @blur="${this._handleOnBlur}"
                  />
                  <span class="value-separator"> - </span>
                  <input
                    id="upperInputText"
                    type="number"
                    min="${this.min}"
                    max="${this.max}"
                    step="${this.step}"
                    .value="${this.upperValue}"
                    maxlength="4"
                    ?disabled="${this.disabled}"
                    @keyup="${this._onKeyPress}"
                    @blur="${this._handleOnBlur}"
                  />`
              : ''}
          </div>`
        : ''}
      <div id="rangeWrapper" class="range-wrapper">
        <div
          id="rangeTrack"
          class="range-track"
          @click="${(e) => this._moveThumbOnRangeTrack(e)}"
          @touchstart="${(e) => this._moveThumbOnRangeTrack(e)}"
        >
          <ul id="range_step_ticks" class="step_ticks">
            ${this.stepMarkers.map(
              (item) => html` <li
                class="sh-step-thumb-item"
                @click="${(e) => this._onTickClick(e)}"
                data-value="${item.position}"
                style="${item.style}"
              ></li>`
            )}
          </ul>
        </div>
        <div
          id="handleTrackWrapper"
          class="handle-track-wrapper"
          tabindex="${this.disabled ? '-1' : '0'}"
          @mousedown="${(e) => this._thumbDown(e)}"
          @touchstart="${(e) => this._thumbDown(e)}"
        >
          <div
            id="handleTrack"
            class="handle-track"
            @mousedown="${(e) => this._thumbDown(e)}"
            @touchstart="${(e) => this._thumbDown(e)}"
          ></div>
        </div>
        <input
          type="range"
          id="lower"
          class="range-track"
          min="${this.min}"
          max="${this.max}"
          label="${this.label}"
          step="${this.step}"
          .value="${this.lowerValue}"
          ?disabled="${this.disabled}"
          @mousedown="${(e) => this._thumbDown(e)}"
          @touchstart="${(e) => this._thumbDown(e)}"
          @input="${(e) => this._thumbOverlapValueCheckRange(e)}"
        />
        <div id="lowerRangeTooltip">${this.lowerValue}</div>
        <input
          type="range"
          id="upper"
          class="range-track"
          min="${this.min}"
          max="${this.max}"
          label="${this.label}"
          step="${this.step}"
          .value="${this.upperValue}"
          ?disabled="${this.disabled}"
          @mousedown="${(e) => this._thumbDown(e)}"
          @touchstart="${(e) => this._thumbDown(e)}"
          @input="${(e) => this._thumbOverlapValueCheckRange(e)}"
        />
        <div id="upperRangeTooltip">${this.upperValue}</div>
      </div>
    `;
  }

  constructor() {
    super();
    this.lowerValue = this.upperValue = 0;
    this.min = 0;
    this.max = 100;
    this.step = 1;
    this.label = 'Label';
    this.stepMarkers = [];
  }

  updated(changedProperties) {
    if (changedProperties.has('step')) {
      this._renderStepMarkersRange();
      this._thumbOverlapLowerValueCheckRange();
      this._thumbOverlapUpperValueCheckRange();
      this._generateHandleTrack();
    }
    if (changedProperties.has('lowerValue')) {
      this._thumbOverlapLowerValueCheckRange();
      this._tooltipSelection();
      this._generateHandleTrack();
    }
    if (changedProperties.has('upperValue')) {
      this._thumbOverlapUpperValueCheckRange();
      this._tooltipSelection();
      this._generateHandleTrack();
    }
    if (changedProperties.has('min') || changedProperties.has('max')) {
      this._renderStepMarkersRange();
      this._thumbOverlapLowerValueCheckRange();
      this._thumbOverlapUpperValueCheckRange();
      this._tooltipSelection();
      this._generateHandleTrack();
    }
    if (changedProperties.has('_isDrag')) {
      this._isDragObserver();
    }
    const listOfProperties = ['lowerValue', 'upperValue'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  connectedCallback() {
    super.connectedCallback();
    // accessing the window events
    this.boundMove = this._thumbMove.bind(this);
    this.boundUp = this._thumbUp.bind(this);
    window.addEventListener('mousemove', this.boundMove);
    window.addEventListener('mouseup', this.boundUp);
    this.addEventListener('touchmove', this.boundMove);
    this.addEventListener('touchend', this.boundUp);
  }

  firstUpdated() {
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
    const lower = this.shadowRoot.querySelector('#lower');
    const upper = this.shadowRoot.querySelector('#upper');
    const handleTrack = this.shadowRoot.querySelector('#handleTrackWrapper');
    const track = this.shadowRoot.querySelector('#handleTrack');
    const sliderToolLower = this.shadowRoot.querySelector('#lowerRangeTooltip');
    const sliderToolUpper = this.shadowRoot.querySelector('#upperRangeTooltip');

    lower.addEventListener('focus', () => this._handleFocus(lower, sliderToolLower));
    lower.addEventListener('blur', () => this._handleBlur(lower, sliderToolLower));
    upper.addEventListener('focus', () => this._handleFocus(upper, sliderToolUpper));
    upper.addEventListener('blur', () => this._handleBlur(upper, sliderToolUpper));

    handleTrack.addEventListener('focus', () =>
      this._handleFocusRangeTrack(
        handleTrack,
        track,
        sliderToolUpper,
        sliderToolLower,
        lower,
        upper
      )
    );
    handleTrack.addEventListener('blur', () =>
      this._handleBlurRangeTrack(handleTrack, track, sliderToolUpper, sliderToolLower, lower, upper)
    );
    handleTrack.addEventListener('mouseover', () => this._addElementFocus(lower, upper));
    handleTrack.addEventListener('mouseout', () => this._removeElementFocus(lower, upper));
  }

  _handleFocus(thumb, tooltip) {
    thumb.onkeyup = (e) => {
      tooltip.classList.add('tab_tooltip');
      if (e.keyCode === KEYCODE.TAB) {
        thumb.classList.add('tab_outline');
      }
      if (e.keyCode === KEYCODE.ARROW_RIGHT || e.keyCode === KEYCODE.ARROW_UP) {
        this._eventTargetSelect(e);
      }
      if (e.keyCode === KEYCODE.ARROW_LEFT || e.keyCode === KEYCODE.ARROW_DOWN) {
        this._eventTargetSelect(e);
      }
    };
  }

  _handleBlur(thumb, tooltip) {
    thumb.classList.remove('tab_outline');
    tooltip.classList.remove('tab_tooltip');
  }

  _handleFocusRangeTrack(handleTrack, track, sliderToolUpper, sliderToolLower, lower, upper) {
    handleTrack.onkeyup = (e) => {
      if (e.keyCode === KEYCODE.TAB) {
        handleTrack.classList.add('tab_outline');
        track.classList.add('tab_outline');
        sliderToolUpper.classList.add('tab_tooltip');
        sliderToolLower.classList.add('tab_tooltip');
        lower.classList.add('tab_outline');
        upper.classList.add('tab_outline');
      }
      if (e.keyCode === KEYCODE.ARROW_RIGHT || e.keyCode === KEYCODE.ARROW_UP) {
        if (upper.value < this.max) {
          lower.value = parseFloat(lower.value) + parseFloat(this.step);
          upper.value = parseFloat(upper.value) + parseFloat(this.step);
          this.lowerValue = Number(lower.value);
          this.upperValue = Number(upper.value);
          this._eventTargetSelect(e);
        }
      }
      if (e.keyCode === KEYCODE.ARROW_LEFT || e.keyCode === KEYCODE.ARROW_DOWN) {
        if (this.lowerValue > this.min) {
          lower.value = parseFloat(lower.value) - parseFloat(this.step);
          upper.value = parseFloat(upper.value) - parseFloat(this.step);
          this.lowerValue = Number(lower.value);
          this.upperValue = Number(upper.value);
          this._eventTargetSelect(e);
        }
      }
    };
  }

  _handleBlurRangeTrack(handleTrack, track, sliderToolUpper, sliderToolLower, lower, upper) {
    handleTrack.classList.remove('tab_outline');
    track.classList.remove('tab_outline');
    sliderToolUpper.classList.remove('tab_tooltip');
    sliderToolLower.classList.remove('tab_tooltip');
    lower.classList.remove('tab_outline');
    upper.classList.remove('tab_outline');
  }

  _eventTargetSelect(e) {
    this._selectedThumb = e.target.id;
    this._dispatchValueChangeEndEvent();
  }

  _addElementFocus(lower, upper) {
    lower.classList.add('focus');
    upper.classList.add('focus');
  }

  _removeElementFocus(lower, upper) {
    lower.classList.remove('focus');
    upper.classList.remove('focus');
  }

  _renderStepMarkersRange() {
    if (this.step === 0) {
      return;
    }
    const stepTickDistance = 32;
    let input, slider_step, containerLength;
    input = this.shadowRoot.querySelector('.range-track');
    slider_step = this.shadowRoot.querySelector('#range_step_ticks');
    containerLength = input.offsetWidth;
    // calculates the distance between two consecutive step-markers
    let ticksObject = this.getCalculatedStepDistance(
      containerLength,
      this.min,
      this.max,
      this.step
    );
    if (ticksObject.distance >= stepTickDistance) {
      slider_step.classList.add('markers');
      // calculates the step-marker points on track and store it in points array
      this.calculatePoints(containerLength);
    } else {
      slider_step.classList.remove('markers');
    }
  }

  _onTickClick(e) {
    e.stopPropagation();
    const upperValue = parseFloat(this.shadowRoot.querySelector('#upper').value);
    const lowerValue = parseFloat(this.shadowRoot.querySelector('#lower').value);
    const tickValue = Number(e.target.dataset.value);
    if (tickValue > upperValue) {
      this.upperValue = tickValue;
    } else if (tickValue < lowerValue) {
      this.lowerValue = tickValue;
    }
  }

  _thumbOverlapValueCheckRange(e) {
    if (e.target.id === 'lower') {
      this._thumbOverlapLowerValueCheckRange();
    } else {
      this._thumbOverlapUpperValueCheckRange();
    }
  }

  _thumbOverlapLowerValueCheckRange() {
    const lower = this.shadowRoot.querySelector('#lower');
    const upper = this.shadowRoot.querySelector('#upper');
    lower.value = Math.min(lower.value, upper.value);
    this._updateLowerValue();
  }

  _thumbOverlapUpperValueCheckRange() {
    const lower = this.shadowRoot.querySelector('#lower');
    const upper = this.shadowRoot.querySelector('#upper');
    upper.value = Math.max(upper.value, lower.value);
    this._updateUpperValue();
  }

  _tooltipSelection() {
    const lower = this.shadowRoot.querySelector('#lower');
    const upper = this.shadowRoot.querySelector('#upper');
    const sliderToolLower = this.shadowRoot.querySelector('#lowerRangeTooltip');
    const sliderToolUpper = this.shadowRoot.querySelector('#upperRangeTooltip');
    this._tooltipCalulations(lower, sliderToolLower);
    this._tooltipCalulations(upper, sliderToolUpper);
  }

  _tooltipCalulations(sliderThumb, tooltip) {
    let newValue = Number(
      ((sliderThumb.value - sliderThumb.min) * 100) / (sliderThumb.max - sliderThumb.min)
    );
    if (Number.isNaN(newValue)) {
      newValue = 0;
    }
    this.setTooltipRange(newValue, tooltip);
  }

  _generateHandleTrack() {
    const handleTrackWrapper = this.shadowRoot.querySelector('#handleTrackWrapper');
    const lower = this.shadowRoot.querySelector('#lower');
    const upper = this.shadowRoot.querySelector('#upper');
    let lowerPosVal, upperPosVal, track, newLowerPosVal, newUpperPosVal;
    lowerPosVal = ((lower.value - this.min) * 100) / (this.max - this.min);
    upperPosVal = ((upper.value - this.min) * 100) / (this.max - this.min);
    track =
      this.shadowRoot.querySelector('#rangeWrapper').getBoundingClientRect().width /
      calculatedBaseFontSize;
    newLowerPosVal = (lowerPosVal / 100) * parseFloat(track);
    newUpperPosVal = (upperPosVal / 100) * parseFloat(track);

    handleTrackWrapper.style.left = parseFloat(newLowerPosVal) + 'rem';
    if (this.upperValue === this.lowerValue) {
      handleTrackWrapper.style.width = '0rem';
    } else {
      handleTrackWrapper.style.width =
        parseFloat(newUpperPosVal) - parseFloat(newLowerPosVal) + 'rem';
    }
  }

  _thumbDown(e) {
    this._isDrag = true;
    this._selectedThumb = e.target.id;
    if (this._selectedThumb === 'handleTrackWrapper' || this._selectedThumb === 'handleTrack') {
      e.preventDefault();
      const offset = this.shadowRoot.querySelector('#handleTrackWrapper').getBoundingClientRect();
      if (e.touches) {
        this.initialX = (e.touches[0].clientX - offset.left) / calculatedBaseFontSize;
      } else {
        this.initialX = (e.clientX - offset.left) / calculatedBaseFontSize;
      }
      this.shadowRoot.querySelector('#handleTrack').classList.add('active');
      this.shadowRoot.querySelector('#upper').classList.add('active');
      this.shadowRoot.querySelector('#lower').classList.add('active');
    } else if (this._selectedThumb === 'lower') {
      this.shadowRoot.querySelector('#lower').classList.add('thumbActive');
    } else if (this._selectedThumb === 'upper') {
      this.shadowRoot.querySelector('#upper').classList.add('thumbActive');
    }
  }

  _thumbMove(e) {
    if (this._isDrag) {
      if (this._selectedThumb === 'handleTrackWrapper' || this._selectedThumb === 'handleTrack') {
        this._moveThumbOnTrack(e);
      }
      if (!this.showValue) {
        this.shadowRoot.querySelector('#lowerRangeTooltip').style.display = 'block';
        this.shadowRoot.querySelector('#upperRangeTooltip').style.display = 'block';
      }
    }
  }

  _thumbUp() {
    if (this._isDrag) {
      this._isDrag = false;
      this.shadowRoot.querySelector('#handleTrack').classList.remove('active');
      this.shadowRoot.querySelector('#lower').classList.remove('active');
      this.shadowRoot.querySelector('#upper').classList.remove('active');
      this.shadowRoot.querySelector('#lower').classList.remove('thumbActive');
      this.shadowRoot.querySelector('#upper').classList.remove('thumbActive');
      this.shadowRoot.querySelector('#lowerRangeTooltip').style.display = '';
      this.shadowRoot.querySelector('#upperRangeTooltip').style.display = '';
    }
  }

  _isDragObserver() {
    if (!this._isDrag) {
      this._dispatchValueChangeEndEvent();
    }
  }

  _moveThumbOnTrack(e) {
    if (e.touches) {
      e.preventDefault();
    }
    this._calculatePositionInTrack(e);
    this._adjustThumbPositionToHandle();
  }

  _calculatePositionInTrack(e) {
    const decimalPoints = this.decimalNumberPrecision();
    const track = this.shadowRoot.querySelector('#rangeWrapper');
    const handle = this.shadowRoot.querySelector('#handleTrackWrapper');
    let offset, maxLength, newThumbPos, handleWidth, handleLeft, checkVal;
    offset = track.getBoundingClientRect();
    maxLength = window.getComputedStyle(track).width.split('p')[0] / calculatedBaseFontSize;
    newThumbPos = (e.clientX - offset.left) / calculatedBaseFontSize;
    //check for touch input
    if (e.touches !== undefined) {
      newThumbPos = (e.touches[0].clientX - offset.left) / calculatedBaseFontSize;
    }
    handleWidth = handle.style.width.split('r')[0];
    handleLeft = handle.style.left.split('r')[0];
    let sliderWidth = parseFloat(maxLength);
    maxLength -= handleWidth;

    newThumbPos -= this.initialX;
    newThumbPos = this._calcPositionForRangeJump(newThumbPos, decimalPoints);
    let upperThumbPos = parseFloat(handleLeft) + parseFloat(handleWidth);
    let tempVal = (parseFloat(upperThumbPos) / sliderWidth) * 100;
    checkVal = (tempVal * (this.max - this.min)) / 100 + this.min;
    checkVal = this._refineValueBasedOnStep(checkVal, decimalPoints);

    // update the thumb position
    switch (newThumbPos != null) {
      case newThumbPos < 0:
        this._setThumbToPosition(0);
        break;
      case newThumbPos > maxLength:
        if (checkVal + this.step <= this.max) {
          this._setThumbToPosition(maxLength);
        }
        break;
      case newThumbPos >= 0 && newThumbPos <= maxLength:
        this._setThumbToPosition(newThumbPos);
        break;
      default:
        console.log('error adjusting thumb');
    }
  }

  _adjustThumbPositionToHandle() {
    const handleTrack = this.shadowRoot.querySelector('#handleTrackWrapper');
    let handleLeft, handleWidth, lowerPosVal, upperPosVal;
    handleLeft = Number(handleTrack.style.left.split('r')[0]);
    handleWidth = Number(handleTrack.style.width.split('r')[0]);
    lowerPosVal = this._thumbPosition(handleLeft);
    upperPosVal = this._thumbPosition(handleLeft + handleWidth);
    this.shadowRoot.querySelector('#lower').value = lowerPosVal;
    this.shadowRoot.querySelector('#upper').value = upperPosVal;
    this._updateLowerValue();
    this._updateUpperValue();
  }

  _thumbPosition(pointOnTrack) {
    const decimalPoints = this.decimalNumberPrecision();
    let posVal = this._calcThumbPosition(pointOnTrack);
    posVal = this._refineValueBasedOnStep(posVal, decimalPoints);
    return posVal;
  }

  _calcThumbPosition(pointOnTrack) {
    let divisor, tempVal, posVal;
    const range = this.shadowRoot.querySelector('#rangeWrapper');
    divisor = window.getComputedStyle(range).width.split('p')[0] / calculatedBaseFontSize;
    tempVal = (pointOnTrack / divisor) * 100;
    posVal = (tempVal * (this.max - this.min)) / 100 + this.min;
    return posVal;
  }

  _calcPositionForRangeJump(newThumbPos, decimalPoints) {
    const track = this.shadowRoot.querySelector('#rangeWrapper');
    let divisor, tempVal, newlowVal, posValue;
    divisor = window.getComputedStyle(track).width.split('p')[0] / calculatedBaseFontSize;
    tempVal = this._calcThumbPosition(newThumbPos);
    newlowVal = this._refineValueBasedOnStep(tempVal, decimalPoints);
    posValue = ((newlowVal - this.min) * 100) / (this.max - this.min);
    newThumbPos = (posValue * divisor) / 100;
    return newThumbPos;
  }

  _refineValueBasedOnStep(tempVal, decimalPoints) {
    let diff, newVal, quotient, num;
    let val = Number(tempVal.toFixed(decimalPoints)) - this.min;
    quotient = Math.round(val / this.step);
    num = this.step * quotient;
    diff = val - num;
    diff = Number(diff.toFixed(decimalPoints));
    newVal = tempVal - diff;
    newVal = Number(newVal.toFixed(decimalPoints));
    return newVal;
  }

  decimalNumberPrecision() {
    let stepInString, minInString;
    stepInString = this.step.toString();
    minInString = this.min.toString();
    let numberOfDecimalPoints;
    numberOfDecimalPoints = 0;
    if (stepInString.includes('.')) {
      numberOfDecimalPoints = getDecimalNumberPrecision(stepInString);
    } else if (minInString.includes('.')) {
      numberOfDecimalPoints = getDecimalNumberPrecision(minInString);
    }
    return numberOfDecimalPoints;
  }

  _setThumbToPosition(newThumbPos) {
    let thumbId, thumb;
    thumbId = this._selectedThumb;
    if (thumbId === 'handleTrack') {
      thumbId = 'handleTrackWrapper';
    }
    thumb = this.shadowRoot.querySelector('#' + thumbId);
    thumb.style.left = newThumbPos + 'rem';
  }

  _moveThumbOnRangeTrack(e) {
    if (e.touches) {
      e.preventDefault();
    }
    if (!this._isDrag) {
      const range = this.shadowRoot.querySelector('#rangeWrapper');
      let offset, pointOnTrack, upPos, posVal;
      offset = range.getBoundingClientRect();
      pointOnTrack = (e.clientX - offset.left) / calculatedBaseFontSize;
      //check for touch input
      if (e.touches !== undefined) {
        pointOnTrack = (e.touches[0].clientX - offset.left) / calculatedBaseFontSize;
      }
      posVal = this._thumbPosition(pointOnTrack);
      if (posVal < this.min) {
        posVal = this.min;
      }
      if (posVal > this.max) {
        posVal = this.max;
      }

      upPos = parseFloat(this.shadowRoot.querySelector('#upper').value);
      if (posVal > upPos) {
        this.shadowRoot.querySelector('#upper').value = posVal;
        this.upperValue = posVal;
        this._selectedThumb = 'upper';
      } else {
        this.shadowRoot.querySelector('#lower').value = posVal;
        this.lowerValue = posVal;
        this._selectedThumb = 'upper';
      }
      this._dispatchValueChangeEndEvent();
    }
  }

  _updateLowerValue() {
    this.lowerValue = Number(this.shadowRoot.querySelector('#lower').value);
  }

  _updateUpperValue() {
    this.upperValue = Number(this.shadowRoot.querySelector('#upper').value);
  }

  _onKeyPress(e) {
    if (
      !(
        e.keyCode === KEYCODE.ARROW_UP ||
        e.keyCode === KEYCODE.ARROW_DOWN ||
        e.keyCode === KEYCODE.ENTER
      )
    ) {
      return;
    }
    let input = e.target;
    this._updateSelectedThumbForValidation(e);
    if (e.keyCode === KEYCODE.ARROW_UP) {
      this._valueValidation(input);
      input.focus();
    }
    if (e.keyCode === KEYCODE.ARROW_DOWN) {
      this._valueValidation(input);
      input.focus();
    }
    // If user press ENTER, then do validation
    if (e.keyCode === KEYCODE.ENTER) {
      this._valueValidation(input);
    }
  }

  _handleOnBlur(e) {
    this._updateSelectedThumbForValidation(e);
    // on blur of input do the validation
    this._valueValidation(e.target);
  }

  _updateSelectedThumbForValidation(e) {
    if (e.target.id === 'lowerInputText') {
      this._selectedThumb = 'lower';
    } else {
      this._selectedThumb = 'upper';
    }
  }

  _valueValidation(input) {
    let value = input.value;
    if (this._selectedThumb === 'lower') {
      input.value = this.getLowerValue(value, this.lowerValue, this.upperValue, this.min, this.max);
      this.lowerValue = Number(input.value);
    } else {
      input.value = this.getUpperValue(value, this.lowerValue, this.upperValue, this.min, this.max);
      this.upperValue = Number(input.value);
    }
    //fire custom-event for input-value changed
    this._dispatchValueChangeEndEvent();
  }

  disconnectedCallback() {
    window.removeEventListener('mousemove', this.boundMove);
    window.removeEventListener('mouseup', this.boundUp);
    super.disconnectedCallback();
  }

  _dispatchValueChangeEndEvent() {
    if (this._selectedThumb === 'handleTrackWrapper' || this._selectedThumb === 'handleTrack') {
      dispatchCustomEvent(this, 'range-value-change-end', {
        lowerValue: this.lowerValue,
        upperValue: this.upperValue,
      });
    } else if (this._selectedThumb === 'lower') {
      dispatchCustomEvent(this, 'value-change-end', {
        lowerValue: this.lowerValue,
      });
    } else {
      dispatchCustomEvent(this, 'value-change-end', {
        upperValue: this.upperValue,
      });
    }
  }
}
customElements.define('range-slider', SHRangeSlider);
